import { styled } from '@abyss/web/tools/styled';
import { Alert } from '@abyss/web/ui/Alert';
import { Table } from '@abyss/web/ui/Table';
import React from 'react';

import {
  BANNER_HOW_TO_USE_MESSAGE,
  COLUMN_DESCRIPTION,
  COLUMN_PROPERTY,
  COLUMN_REQUIRED,
  COLUMN_REQUIRED_IF,
  COLUMN_VALUES_EXAMPLES,
  DESC_CLIENT_NAME,
  DESC_CLUSTER_KEY,
  DESC_COVERAGE_TYPE,
  DESC_CUSTOM_BRANDING,
  DESC_FIPS_CODE,
  DESC_LANGUAGE,
  DESC_LOB,
  DESC_PLAN_ID,
  DESC_PLAN_NAME,
  DESC_PLAN_YEAR,
  DESC_POLICY_NUMBER,
  DESC_PORTAL_SOURCE,
  DESC_PV_RC,
  DESC_RECIPROCITY_ID,
  DESC_SAVE_WITHOUT_VALIDATION,
  DESC_SHOW_SIGN_IN,
  DESC_STATE_CODE,
  INPUT_TYPE_DROPDOWN,
  INPUT_TYPE_FREE_TEXT,
  INPUT_TYPE_RADIO,
  LABEL_CLIENT_NAME,
  LABEL_CLUSTER_KEY,
  LABEL_COVERAGE_TYPE,
  LABEL_CUSTOM_BRANDING,
  LABEL_FIPS_CODE,
  LABEL_LANGUAGE,
  LABEL_LOB,
  LABEL_PLAN_ID,
  LABEL_PLAN_NAME,
  LABEL_PLAN_YEAR,
  LABEL_POLICY_NUMBER,
  LABEL_PORTAL_SOURCE,
  LABEL_PV_RC,
  LABEL_RECIPROCITY_ID,
  LABEL_SAVE_WITHOUT_VALIDATION,
  LABEL_SHOW_SIGN_IN,
  LABEL_STATE_CODE,
  LEGEND_CONDITIONAL_DESC,
  LEGEND_OPTIONAL_DESC,
  LEGEND_REQUIRED_DESC,
  LEGEND_TITLE,
  REQUIRED_IF_ALWAYS,
  REQUIRED_IF_CLIENT_NAME,
  REQUIRED_IF_COVERAGE_TYPE,
  REQUIRED_IF_LOB,
  REQUIRED_IF_NONE,
  REQUIRED_IF_RECIPROCITY_ID,
  STATUS_CONDITIONAL,
  STATUS_OPTIONAL,
  STATUS_REQUIRED,
  TITLE_FIELD_REQUIREMENTS,
  TITLE_HOW_TO_USE,
  VALUES_CLIENT_NAME,
  VALUES_CLUSTER_KEY,
  VALUES_COVERAGE_TYPE,
  VALUES_CUSTOM_BRANDING,
  VALUES_FIPS_CODE,
  VALUES_LANGUAGE,
  VALUES_LOB,
  VALUES_PLAN_ID,
  VALUES_PLAN_NAME,
  VALUES_PLAN_YEAR,
  VALUES_POLICY_NUMBER,
  VALUES_PORTAL_SOURCE,
  VALUES_PV_RC,
  VALUES_RECIPROCITY_ID,
  VALUES_SAVE_WITHOUT_VALIDATION,
  VALUES_SHOW_SIGN_IN,
  VALUES_STATE_CODE,
} from './constants';

const TableWrapper = styled('div', {
  fontFamily: '$primary',
  backgroundColor: '$bgHeaderColor',
  padding: '1em',

  '.abyss-table-root': {
    backgroundColor: '$white',
    border: '1px solid $gray3',
    borderRadius: '4px',
    overflow: 'hidden',
  },

  '.abyss-table-head': {
    backgroundColor: '$primary1',
  },

  '.abyss-table-header-row': {
    backgroundColor: '$primary1',
  },

  '.abyss-table-column-header': {
    backgroundColor: '$primary1',
    color: '$white',
    fontWeight: 'bold',
    padding: '12px',
    fontSize: '14px',
    textAlign: 'left',
    borderBottom: '2px solid $gray3',
  },

  '.abyss-table-body': {
    backgroundColor: '$white',
  },

  '.abyss-table-row': {
    '&:nth-child(even)': {
      backgroundColor: '$gray1',
    },
    '&:hover': {
      backgroundColor: '$gray2',
    },
  },

  '.abyss-table-cell': {
    padding: '12px',
    borderBottom: '1px solid $gray3',
    fontSize: '13px',
    verticalAlign: 'top',
    lineHeight: '1.5',
  },
});

const InstructionsBanner = styled(Alert, {
  '& .abyss-alert-icon': {
    fontSize: '20px !important',
    margin: '8px',
  },
  '& .abyss-alert-container': {
    marginBottom: '24px',
    borderColor: '$interactive1',
    backgroundColor: '$info2',
  },

  '& .abyss-alert-children-container, & .abyss-alert-title': {
    fontSize: '14px',
    fontWeight: 400,
    textAlign: 'left',
    padding: '8px',
  },
  '& .abyss-alert-title': { fontWeight: 700, padding: '8px' },
});

const LegendContainer = styled('div', {
  marginTop: '20px',
  padding: '12px',
  backgroundColor: '$gray1',
  borderRadius: '4px',
  border: '1px solid $gray3',
});

const LegendTitle = styled('p', {
  margin: 0,
  fontWeight: 'bold',
  marginBottom: '8px',
  color: '$primary1',
});

const LegendList = styled('ul', {
  margin: 0,
  paddingLeft: '20px',
  fontSize: '13px',

  li: {
    marginBottom: '4px',
  },
});

const Required = styled('span', {
  color: '$error',
  fontWeight: 'bold',
});

const Conditional = styled('span', {
  color: '$warning',
  fontWeight: 'bold',
});

const Optional = styled('span', {
  color: '$gray6',
});

const FieldName = styled('strong', {
  color: '$primary1',
});

export const InstructionsTable = () => {
  const columns = [
    { key: 'property', name: COLUMN_PROPERTY },
    { key: 'required', name: COLUMN_REQUIRED },
    { key: 'requiredIf', name: COLUMN_REQUIRED_IF },
    { key: 'values', name: COLUMN_VALUES_EXAMPLES },
    { key: 'description', name: COLUMN_DESCRIPTION },
  ];

  const rows = [
    {
      id: 1,
      property: <FieldName>{LABEL_LANGUAGE}</FieldName>,
      required: <Required>{STATUS_REQUIRED}</Required>,
      requiredIf: REQUIRED_IF_ALWAYS,
      inputType: INPUT_TYPE_DROPDOWN,
      values: VALUES_LANGUAGE,
      description: DESC_LANGUAGE,
    },
    {
      id: 2,
      property: <FieldName>{LABEL_LOB}</FieldName>,
      required: <Conditional>{STATUS_CONDITIONAL}</Conditional>,
      requiredIf: REQUIRED_IF_LOB,
      inputType: INPUT_TYPE_DROPDOWN,
      values: VALUES_LOB,
      description: DESC_LOB,
    },
    {
      id: 3,
      property: <FieldName>{LABEL_COVERAGE_TYPE}</FieldName>,
      required: <Conditional>{STATUS_CONDITIONAL}</Conditional>,
      requiredIf: REQUIRED_IF_COVERAGE_TYPE,
      inputType: INPUT_TYPE_DROPDOWN,
      values: VALUES_COVERAGE_TYPE,
      description: DESC_COVERAGE_TYPE,
    },
    {
      id: 4,
      property: <FieldName>{LABEL_RECIPROCITY_ID}</FieldName>,
      required: <Conditional>{STATUS_CONDITIONAL}</Conditional>,
      requiredIf: REQUIRED_IF_RECIPROCITY_ID,
      inputType: INPUT_TYPE_FREE_TEXT,
      values: VALUES_RECIPROCITY_ID,
      description: DESC_RECIPROCITY_ID,
    },
    {
      id: 5,
      property: <FieldName>{LABEL_CLIENT_NAME}</FieldName>,
      required: <Conditional>{STATUS_CONDITIONAL}</Conditional>,
      requiredIf: REQUIRED_IF_CLIENT_NAME,
      inputType: INPUT_TYPE_FREE_TEXT,
      values: VALUES_CLIENT_NAME,
      description: DESC_CLIENT_NAME,
    },
    {
      id: 6,
      property: <FieldName>{LABEL_PLAN_NAME}</FieldName>,
      required: <Optional>{STATUS_OPTIONAL}</Optional>,
      requiredIf: REQUIRED_IF_NONE,
      inputType: INPUT_TYPE_FREE_TEXT,
      values: VALUES_PLAN_NAME,
      description: DESC_PLAN_NAME,
    },
    {
      id: 7,
      property: <FieldName>{LABEL_PLAN_ID}</FieldName>,
      required: <Optional>{STATUS_OPTIONAL}</Optional>,
      requiredIf: REQUIRED_IF_NONE,
      inputType: INPUT_TYPE_FREE_TEXT,
      values: VALUES_PLAN_ID,
      description: DESC_PLAN_ID,
    },
    {
      id: 8,
      property: <FieldName>{LABEL_CUSTOM_BRANDING}</FieldName>,
      required: <Optional>{STATUS_OPTIONAL}</Optional>,
      requiredIf: REQUIRED_IF_NONE,
      inputType: INPUT_TYPE_DROPDOWN,
      values: VALUES_CUSTOM_BRANDING,
      description: DESC_CUSTOM_BRANDING,
    },
    {
      id: 9,
      property: <FieldName>{LABEL_POLICY_NUMBER}</FieldName>,
      required: <Optional>{STATUS_OPTIONAL}</Optional>,
      requiredIf: REQUIRED_IF_NONE,
      inputType: INPUT_TYPE_FREE_TEXT,
      values: VALUES_POLICY_NUMBER,
      description: DESC_POLICY_NUMBER,
    },
    {
      id: 10,
      property: <FieldName>{LABEL_FIPS_CODE}</FieldName>,
      required: <Optional>{STATUS_OPTIONAL}</Optional>,
      requiredIf: REQUIRED_IF_NONE,
      inputType: INPUT_TYPE_FREE_TEXT,
      values: VALUES_FIPS_CODE,
      description: DESC_FIPS_CODE,
    },
    {
      id: 11,
      property: <FieldName>{LABEL_CLUSTER_KEY}</FieldName>,
      required: <Optional>{STATUS_OPTIONAL}</Optional>,
      requiredIf: REQUIRED_IF_NONE,
      inputType: INPUT_TYPE_FREE_TEXT,
      values: VALUES_CLUSTER_KEY,
      description: DESC_CLUSTER_KEY,
    },
    {
      id: 12,
      property: <FieldName>{LABEL_PORTAL_SOURCE}</FieldName>,
      required: <Optional>{STATUS_OPTIONAL}</Optional>,
      requiredIf: REQUIRED_IF_NONE,
      inputType: INPUT_TYPE_FREE_TEXT,
      values: VALUES_PORTAL_SOURCE,
      description: DESC_PORTAL_SOURCE,
    },
    {
      id: 13,
      property: <FieldName>{LABEL_PV_RC}</FieldName>,
      required: <Optional>{STATUS_OPTIONAL}</Optional>,
      requiredIf: REQUIRED_IF_NONE,
      inputType: INPUT_TYPE_FREE_TEXT,
      values: VALUES_PV_RC,
      description: DESC_PV_RC,
    },
    {
      id: 14,
      property: <FieldName>{LABEL_SAVE_WITHOUT_VALIDATION}</FieldName>,
      required: <Optional>{STATUS_OPTIONAL}</Optional>,
      requiredIf: REQUIRED_IF_NONE,
      inputType: INPUT_TYPE_RADIO,
      values: VALUES_SAVE_WITHOUT_VALIDATION,
      description: DESC_SAVE_WITHOUT_VALIDATION,
    },
    {
      id: 15,
      property: <FieldName>{LABEL_SHOW_SIGN_IN}</FieldName>,
      required: <Optional>{STATUS_OPTIONAL}</Optional>,
      requiredIf: REQUIRED_IF_NONE,
      inputType: INPUT_TYPE_DROPDOWN,
      values: VALUES_SHOW_SIGN_IN,
      description: DESC_SHOW_SIGN_IN,
    },
    {
      id: 16,
      property: <FieldName>{LABEL_PLAN_YEAR}</FieldName>,
      required: <Optional>{STATUS_OPTIONAL}</Optional>,
      requiredIf: REQUIRED_IF_NONE,
      inputType: INPUT_TYPE_FREE_TEXT,
      values: VALUES_PLAN_YEAR,
      description: DESC_PLAN_YEAR,
    },
    {
      id: 17,
      property: <FieldName>{LABEL_STATE_CODE}</FieldName>,
      required: <Optional>{STATUS_OPTIONAL}</Optional>,
      requiredIf: REQUIRED_IF_NONE,
      inputType: INPUT_TYPE_FREE_TEXT,
      values: VALUES_STATE_CODE,
      description: DESC_STATE_CODE,
    },
  ];

  return (
    <React.Fragment>
      <InstructionsBanner
        data-testid="instructions-banner"
        title={TITLE_HOW_TO_USE}
        variant="info"
      >
        {BANNER_HOW_TO_USE_MESSAGE}
      </InstructionsBanner>

      <TableWrapper>
        <Table
          columns={columns}
          highlightRowOnHover
          rows={rows}
          title={TITLE_FIELD_REQUIREMENTS}
        />

        <LegendContainer>
          <LegendTitle>{LEGEND_TITLE}</LegendTitle>
          <LegendList>
            <li>
              <Required>{STATUS_REQUIRED}</Required> - {LEGEND_REQUIRED_DESC}
            </li>
            <li>
              <Conditional>{STATUS_CONDITIONAL}</Conditional> -{' '}
              {LEGEND_CONDITIONAL_DESC}
            </li>
            <li>
              <Optional>{STATUS_OPTIONAL}</Optional> - {LEGEND_OPTIONAL_DESC}
            </li>
          </LegendList>
        </LegendContainer>
      </TableWrapper>
    </React.Fragment>
  );
};
